import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { NoticeBoardPost, NoticeCategory, NoticeStatus, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';
import { NoticeDetail } from './NoticeDetail';

const STATUS_OPTIONS: NoticeStatus[] = ['DRAFT', 'PUBLISHED', 'WITHDRAWN'];

const CATEGORY_OPTIONS: NoticeCategory[] = ['GENERAL', 'MEETING_NOTICE', 'MINUTES', 'BYLAW', 'EMERGENCY_ALERT', 'EVENT', 'CONSULTATION'];

/**
 * Notice board publishing — GET /notices?district=<own district>&status=
 * (the `status` filter is only honoured by the API when the caller is that
 * district's own staff, which we always are here). MINUTES/BYLAW posts get
 * real DocVault issuance when a document reference is attached at publish
 * time; BYLAW additionally publishes to C11 OpenGov (see NoticeDetail.tsx).
 */
export function NoticesConsole() {
  const { persona } = useAuth();
  const district = persona?.district ?? '';
  const [status, setStatus] = useState<NoticeStatus | ''>('');
  const [category, setCategory] = useState<NoticeCategory | ''>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const qs = new URLSearchParams({ limit: '100' });
  if (district) qs.set('district', district);
  if (status) qs.set('status', status);
  if (category) qs.set('category', category);
  const noticesQ = useApiQuery<Page<NoticeBoardPost>>(district ? `/api/v1/m41/notices?${qs.toString()}` : null);
  const notices = noticesQ.data?.items ?? [];

  if (!district) return <ErrorNote message="This account has no district binding on file." />;

  return (
    <div>
      <PageHeader
        title="Notice Board"
        subtitle="Draft, publish and withdraw this district's digital notice board (also fed to physical office screens)."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New notice
          </button>
        }
      />

      <div className="mb-4 flex flex-wrap gap-3">
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as NoticeStatus | '')}>
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
        <select className="field-input !w-auto" value={category} onChange={(e) => setCategory(e.target.value as NoticeCategory | '')}>
          <option value="">All categories</option>
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {c.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
      </div>

      {noticesQ.error ? (
        <div className="mb-4">
          <ErrorNote message={noticesQ.error} />
        </div>
      ) : null}

      <div className="card overflow-x-auto">
        {noticesQ.loading ? (
          <Spinner />
        ) : notices.length === 0 ? (
          <EmptyState title="No notices match these filters" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Title</th>
                <th className="table-head">Category</th>
                <th className="table-head">Pinned</th>
                <th className="table-head">On screen</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {notices.map((n) => (
                <tr key={n.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(n.id)}>
                  <td className="table-cell font-medium">{n.title}</td>
                  <td className="table-cell text-xs text-slate-500">{n.category.replaceAll('_', ' ')}</td>
                  <td className="table-cell">{n.pinned ? '☑' : '—'}</td>
                  <td className="table-cell">{n.showOnPhysicalScreen ? '☑' : '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={n.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <NoticeDetail id={selectedId} onClose={() => setSelectedId(null)} onChanged={() => noticesQ.reload()} />
      ) : null}
      {showCreate ? (
        <CreateNoticeModal
          district={district}
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            noticesQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateNoticeModal({ district, onClose, onDone }: { district: string; onClose: () => void; onDone: () => void }) {
  const [category, setCategory] = useState<NoticeCategory>('GENERAL');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [pinned, setPinned] = useState(false);
  const [showOnPhysicalScreen, setShowOnPhysicalScreen] = useState(true);
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m41/notices', {
        district,
        category,
        title: title.trim(),
        body: body.trim(),
        pinned,
        showOnPhysicalScreen,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create notice');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New notice" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value as NoticeCategory)}>
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {c.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Expires (optional)">
            <input className="field-input" type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
          </Field>
        </div>
        <Field label="Title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Body">
          <textarea className="field-input" rows={5} value={body} onChange={(e) => setBody(e.target.value)} />
        </Field>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} />
            Pinned
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={showOnPhysicalScreen} onChange={(e) => setShowOnPhysicalScreen(e.target.checked)} />
            Show on physical office screen
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || title.trim().length < 2 || body.trim().length < 2}>
            {busy ? 'Saving…' : 'Save draft'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
